import BottomNavBar from "../../components/BottomNavBar";
import { RightOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import { useEffect, useState } from "react";
import mealApiService from "../../services/MealApiService";
import welcomeBg from "../../assets/homebg.png";
import {
  Calendar,
  Coffee,
  Sun,
  Apple,
  Moon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";

interface FoodItem {
  name: string;
  serving_size_g: number;
  serving_description: string;
  carbohydrate_content_g: number;
  dietary_fiber_g: number;
  net_carbs_g: number;
  sugar_g: number;
  protein_content_g: number;
  fat_content_g: number;
  trans_fat_g: number;
  saturated_fat_g: number;
  calorie_content_kcal: number;
  GI_estimate: number;
  GL_estimate: number;
}

interface MealTotals {
  carbohydrate_content_g: number;
  dietary_fiber_g: number;
  net_carbs_g: number;
  sugar_g: number;
  protein_content_g: number;
  fat_content_g: number;
  trans_fat_g: number;
  saturated_fat_g: number;
  calorie_content_kcal: number;
  GI_estimate: number;
  GL_estimate: number;
}

interface MealData {
  document_id?: string;
  id?: string;
  date: string;
  food_items: FoodItem[];
  totals: MealTotals;
  image_url?: string;
  image?: string;
  file_url?: string;
  document?: string;
  meal_type?: string;
  ghi_score?: number;
  ghi_label?: string;
}

interface GroupedMeals {
  breakfast: MealData[];
  lunch: MealData[];
  snack: MealData[];
  dinner: MealData[];
}

// Utility function to group meals by type based on meal_type field
const groupMealsByType = (meals: MealData[]): GroupedMeals => {
  const grouped: GroupedMeals = {
    breakfast: [],
    lunch: [],
    snack: [],
    dinner: [],
  };

  meals.forEach(meal => {
    if (meal.meal_type) {
      const type = meal.meal_type.toLowerCase();

      if (type === "breakfast") {
        grouped.breakfast.push(meal);
      } else if (type === "lunch") {
        grouped.lunch.push(meal);
      } else if (type === "snack" || type === "snacks") {
        grouped.snack.push(meal);
      } else if (type === "dinner") {
        grouped.dinner.push(meal);
      } else {
        grouped.snack.push(meal); // Default to snack for 'other'
      }
    }
  });

  return grouped;
};

const MealPage = () => {
  const navigate = useNavigate();
  const [todayBackendTotals, setTodayBackendTotals] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const getLocalDateString = (date: Date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [selectedDate, setSelectedDate] =
    useState<string>(getLocalDateString());

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectedDateTotals, setSelectedDateTotals] = useState<any>(null);
  const [mealGoals, setMealGoals] = useState<any>({
    calories_target: 2000,
    protein_g: 150,
    fat_g: 65,
    carbs_g: 200,
    fiber_g: 30,
  });
  const [groupedMeals, setGroupedMeals] = useState<GroupedMeals>({
    breakfast: [],
    lunch: [],
    snack: [],
    dinner: [],
  });

  // Fetch meal data function
  const fetchMealDataForDate = async (dateString: string) => {
    try {
      const response = await mealApiService.getMealDataByDate(dateString);

      let mealsArray: any[] = [];
      let backendTotals = null;

      // Check if response itself is the data (not wrapped in response.data)
      const actualData = response?.data ?? response;

      if (actualData) {
        // Use backend provided totals directly
        if (
          actualData.total_net_carb !== undefined ||
          actualData.total_carbohydrate !== undefined ||
          actualData.total_protein !== undefined ||
          actualData.total_fat !== undefined ||
          actualData.total_fiber !== undefined ||
          actualData.total_calorie !== undefined
        ) {
          backendTotals = {
            calories: actualData.total_calorie ?? 0,
            carbs: actualData.total_carbohydrate ?? 0,
            net_carbs: actualData.total_net_carb ?? 0,
            protein: actualData.total_protein ?? 0,
            fat: actualData.total_fat ?? 0,
            fiber: actualData.total_fiber ?? 0,
          };
        }

        // Try multiple possible response structures
        if (actualData.meal_data && Array.isArray(actualData.meal_data)) {
          mealsArray = actualData.meal_data;
        } else if (actualData.results && Array.isArray(actualData.results)) {
          mealsArray = actualData.results;
        } else if (actualData.data && Array.isArray(actualData.data)) {
          mealsArray = actualData.data;
        } else if (Array.isArray(actualData)) {
          mealsArray = actualData as any;
        } else if (actualData.meals && Array.isArray(actualData.meals)) {
          mealsArray = actualData.meals;
        }

        // Transform meals to consistent format
        mealsArray = mealsArray.map((meal: any) => ({
          ...meal,
          meal_type: meal.meal_type, // Preserve meal_type from API
          totals: meal.totals || {
            carbohydrate_content_g: 0,
            dietary_fiber_g: 0,
            net_carbs_g: 0,
            sugar_g: 0,
            protein_content_g: 0,
            fat_content_g: 0,
            trans_fat_g: 0,
            saturated_fat_g: 0,
            calorie_content_kcal: 0,
            GI_estimate: 0,
            GL_estimate: 0,
          },
          food_items: meal.food_items || [],
          image_url: meal.image_url ?? meal.document,
          date: meal.date,
          ghi_score: meal.ghi_score || 0,
          ghi_label: meal.ghi_label || "",
        }));
      }

      return {
        meals: mealsArray,
        totals: backendTotals,
        goals: null,
        apiDate: actualData.date_local,
      };
    } catch (error: any) {
      return { meals: [], totals: null, goals: null, apiDate: null };
    }
  };

  const [apiDate, setApiDate] = useState<string | null>(null);

  useEffect(() => {
    const loadTodayData = async () => {
      setLoading(true);

      const today = getLocalDateString();

      const todayResult = await fetchMealDataForDate(today);

      setTodayBackendTotals(todayResult.totals);

      if (todayResult.apiDate) {
        setApiDate(todayResult.apiDate);
      }

      // Set meal goals from API
      if (todayResult.goals) {
        setMealGoals(todayResult.goals);
      }

      // Set initial selected date totals from backend
      const initialTotals = todayResult.totals ?? {
        calories: 0,
        carbs: 0,
        protein: 0,
        fat: 0,
        fiber: 0,
      };
      setSelectedDateTotals(initialTotals);

      // Direct API call to ensure meals load properly
      try {
        const directResponse = await mealApiService.getMealDataByDate(today);

        if (
          directResponse?.meal_data &&
          Array.isArray(directResponse.meal_data)
        ) {
          const directGrouped = groupMealsByType(directResponse.meal_data);
          setGroupedMeals(directGrouped);

          // Set totals from direct response
          if (directResponse.total_calorie !== undefined) {
            const directTotals = {
              calories: directResponse.total_calorie ?? 0,
              carbs: directResponse.total_carbohydrate ?? 0,
              net_carbs: directResponse.total_net_carb ?? 0,
              protein: directResponse.total_protein ?? 0,
              fat: directResponse.total_fat ?? 0,
              fiber: directResponse.total_fiber ?? 0,
            };
            setTodayBackendTotals(directTotals);
            setSelectedDateTotals(directTotals);
          }

          // Set goals from direct response
          if (directResponse.meal_goals) {
            const directGoals = {
              calories_target:
                directResponse.meal_goals.calories_target ?? 2000,
              protein_g: directResponse.meal_goals.protein_g ?? 150,
              fat_g: directResponse.meal_goals.fat_g ?? 65,
              carbs_g: directResponse.meal_goals.carbs_g ?? 200,
              fiber_g: directResponse.meal_goals.fiber_g ?? 30,
            };
            setMealGoals(directGoals);
          }

          setLoading(false);
          return;
        }
      } catch (directError) {
        // Silent fail, use fallback
      }

      // Fallback - Set initial grouped meals for today
      const grouped = groupMealsByType(todayResult.meals);
      setGroupedMeals(grouped);
      setSelectedDate(today);
      setLoading(false);
    };

    loadTodayData();
  }, []);

  // Remove this useEffect as it's interfering with date selection
  // useEffect(() => {
  //   const today = getLocalDateString();
  //   if (selectedDate === today && mealData.length > 0 && todayBackendTotals) {
  //     if (!selectedDateTotals) {
  //       setGroupedMeals(groupMealsByType(mealData));
  //       setSelectedDateTotals(todayBackendTotals);
  //     }
  //   }
  // }, [selectedDate, mealData, todayBackendTotals, selectedDateTotals]);

  const todayTotals = todayBackendTotals ?? {
    calories: 0,
    carbs: 0,
    net_carbs: 0,
    protein: 0,
    fat: 0,
    fiber: 0,
  };

  const getTotalsForDate = (dateString: string) => {
    if (selectedDateTotals) {
      return selectedDateTotals;
    }

    const today = getLocalDateString();
    if (dateString === today) {
      return todayTotals;
    }
    return {
      calories: 0,
      carbs: 0,
      net_carbs: 0,
      protein: 0,
      fat: 0,
      fiber: 0,
    };
  };

  const handleMealTypeClick = (mealType: string, meals: MealData[]) => {
    if (meals.length === 0) {
      // Navigate to food scanning if no meals
      navigate(`/glucera${GluceraNavigationRoutes.HOMEPAGE}`, {
        state: {
          mealType: mealType.charAt(0).toUpperCase() + mealType.slice(1),
          fromMealPage: true,
        },
      });
      return;
    }

    if (meals.length === 1) {
      // If only one meal, go directly to detail
      navigate(`/glucera${GluceraNavigationRoutes.mealdetailpage}`, {
        state: {
          mealId: meals[0].document_id,
          mealNumber: 1,
          mealType: mealType.charAt(0).toUpperCase() + mealType.slice(1), // Add proper meal type
          mealItems: meals[0].food_items,
          mealTotals: meals[0].totals,
          mealDate: meals[0].date,
          mealImage: meals[0].image_url ?? meals[0].document,
          fromMealPage: true,
        },
      });
    } else {
      // Multiple meals - navigate to meal detail with all meals of this type
      navigate(`/glucera${GluceraNavigationRoutes.mealdetailpage}`, {
        state: {
          mealType: mealType.charAt(0).toUpperCase() + mealType.slice(1),
          mealItems: meals.flatMap(meal => meal.food_items),
          mealTotals: meals.reduce(
            (acc, meal) => ({
              carbohydrate_content_g:
                acc.carbohydrate_content_g +
                (meal.totals.carbohydrate_content_g || 0),
              dietary_fiber_g:
                acc.dietary_fiber_g + (meal.totals.dietary_fiber_g || 0),
              net_carbs_g: acc.net_carbs_g + (meal.totals.net_carbs_g || 0),
              sugar_g: acc.sugar_g + (meal.totals.sugar_g || 0),
              protein_content_g:
                acc.protein_content_g + (meal.totals.protein_content_g || 0),
              fat_content_g:
                acc.fat_content_g + (meal.totals.fat_content_g || 0),
              trans_fat_g: acc.trans_fat_g + (meal.totals.trans_fat_g || 0),
              saturated_fat_g:
                acc.saturated_fat_g + (meal.totals.saturated_fat_g || 0),
              calorie_content_kcal:
                acc.calorie_content_kcal +
                (meal.totals.calorie_content_kcal || 0),
              GI_estimate: acc.GI_estimate + (meal.totals.GI_estimate || 0),
              GL_estimate: acc.GL_estimate + (meal.totals.GL_estimate || 0),
            }),
            {
              carbohydrate_content_g: 0,
              dietary_fiber_g: 0,
              net_carbs_g: 0,
              sugar_g: 0,
              protein_content_g: 0,
              fat_content_g: 0,
              trans_fat_g: 0,
              saturated_fat_g: 0,
              calorie_content_kcal: 0,
              GI_estimate: 0,
              GL_estimate: 0,
            }
          ),
          mealDate: meals[0]?.date,
          fromMealPage: true,
          allMeals: meals,
        },
      });
    }
  };

  // Date handling functions
  const handleDateSelect = async (dateString: string) => {
    // Check if selected date is in the future
    const selectedDateObj = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDateObj.setHours(0, 0, 0, 0);

    if (selectedDateObj > today) {
      return;
    }

    setSelectedDate(dateString);
    setApiDate(null); // Clear previous API date to show selected date immediately
    setIsCalendarOpen(false);
    setLoading(true);

    try {
      const response = await mealApiService.getMealDataByDate(dateString);

      // Check different response structures
      const actualData = response?.data ?? response;

      let mealsArray = [];
      let totalsData = null;

      // Extract meals array
      if (actualData?.meal_data && Array.isArray(actualData.meal_data)) {
        mealsArray = actualData.meal_data;
      } else if (actualData?.results && Array.isArray(actualData.results)) {
        mealsArray = actualData.results;
      } else if (Array.isArray(actualData)) {
        mealsArray = actualData;
      } else if (actualData?.meals && Array.isArray(actualData.meals)) {
        mealsArray = actualData.meals;
      }

      // Extract totals
      if (actualData) {
        totalsData = {
          calories: actualData.total_calorie ?? actualData.total_calories ?? 0,
          carbs: actualData.total_carbohydrate ?? 0,
          net_carbs: actualData.total_net_carb ?? 0,
          protein: actualData.total_protein ?? 0,
          fat: actualData.total_fat ?? 0,
          fiber: actualData.total_fiber ?? 0,
        };
      }

      // Group meals by type
      const groupedMealsForDate = groupMealsByType(mealsArray);

      // Update state
      setGroupedMeals(groupedMealsForDate);
      setSelectedDateTotals(totalsData);
      setApiDate(actualData.date_local || dateString);

      // Update goals if available
      if (actualData?.meal_goals) {
        setMealGoals({
          calories_target: actualData.meal_goals.calories_target ?? 2000,
          protein_g: actualData.meal_goals.protein_g ?? 150,
          fat_g: actualData.meal_goals.fat_g ?? 65,
          carbs_g: actualData.meal_goals.carbs_g ?? 200,
          fiber_g: actualData.meal_goals.fiber_g ?? 30,
        });
      }
    } catch (error) {
      // Set empty state if API call fails
      setGroupedMeals({
        breakfast: [],
        lunch: [],
        snack: [],
        dinner: [],
      });
      setSelectedDateTotals({
        calories: 0,
        carbs: 0,
        net_carbs: 0,
        protein: 0,
        fat: 0,
        fiber: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const navigateMonth = (direction: "prev" | "next") => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === "prev") {
        newMonth.setMonth(prev.getMonth() - 1);
      } else {
        newMonth.setMonth(prev.getMonth() + 1);
      }
      return newMonth;
    });
  };

  // Helper functions for meal UI
  const getMealIcon = (mealType: string) => {
    switch (mealType.toLowerCase()) {
      case "breakfast":
        return <Coffee className="h-6 w-6 text-orange-500" />;
      case "lunch":
        return <Sun className="h-6 w-6 text-yellow-500" />;
      case "snack":
        return <Apple className="h-6 w-6 text-purple-500" />;
      case "dinner":
        return <Moon className="h-6 w-6 text-blue-500" />;
      default:
        return <Apple className="h-6 w-6 text-muted-foreground" />;
    }
  };

  const getMealTime = (_mealType: string, meals: MealData[]) => {
    if (meals.length === 0) {
      return ""; // Don't show any time if no meal exists
    }

    // Convert time to 12-hour format
    try {
      const rawDate = meals[0].date;

      // Parse different date/time formats
      if (typeof rawDate === "string") {
        let timeString = "";

        // Extract time part if it's a full datetime string
        if (rawDate.includes(" ") && rawDate.includes(":")) {
          timeString = rawDate.split(" ")[1]; // Get time part after space
        } else if (rawDate.includes("T") && rawDate.includes(":")) {
          timeString = rawDate.split("T")[1].split("Z")[0]; // ISO format
        } else if (rawDate.includes(":") && !rawDate.includes("-")) {
          timeString = rawDate; // Already just time
        } else {
          // Parse as full date and extract time
          const date = new Date(rawDate);
          return date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }

        // Convert 24-hour to 12-hour format
        if (timeString.includes(":")) {
          const [hours, minutes] = timeString.split(":");
          const hour24 = parseInt(hours);
          const hour12 = hour24 > 12 ? hour24 - 12 : hour24 === 0 ? 12 : hour24;
          const ampm = hour24 >= 12 ? "PM" : "AM";
          return `${hour12}:${minutes.padStart(2, "0")} ${ampm}`;
        }
      }

      // Fallback - parse as date object
      const date = new Date(rawDate);
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      return ""; // Don't show time if parsing fails
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="text-lg text-muted-foreground">Loading meals...</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image - Only within container */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast - Only within container */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 80px)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between pb-4 pt-2">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {selectedDate === getLocalDateString()
                ? "Today's Meals"
                : "Meals"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {apiDate
                ? new Date(apiDate).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  timeZone: "UTC",
                })
                : new Date(selectedDate).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}
            </p>
          </div>
          <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 rounded-xl border-border bg-card hover:bg-muted"
              >
                <Calendar className="h-5 w-5 text-orange-500" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-80 p-4"
              align="end"
              side="bottom"
              sideOffset={8}
            >
              <div className="space-y-4">
                {/* Calendar Header */}
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => navigateMonth("prev")}
                    className="h-8 w-8"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <h3 className="text-lg font-semibold">
                    {currentMonth.toLocaleDateString("en-US", {
                      month: "long",
                      year: "numeric",
                    })}
                  </h3>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => navigateMonth("next")}
                    className="h-8 w-8"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {/* Day headers */}
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                    day => (
                      <div
                        key={day}
                        className="py-2 text-center text-sm font-medium text-muted-foreground"
                      >
                        {day}
                      </div>
                    )
                  )}

                  {/* Calendar days */}
                  {getDaysInMonth(currentMonth).map((date, index) => {
                    if (!date) {
                      return <div key={index} className="h-8" />;
                    }

                    // 🔧 Fix: Use local timezone instead of UTC
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    const day = String(date.getDate()).padStart(2, "0");
                    const dateString = `${year}-${month}-${day}`;

                    const isSelected = dateString === selectedDate;

                    // Fix today comparison too
                    const today = new Date();
                    const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
                    const isToday = dateString === todayString;

                    // Check if date is in future
                    const dateObj = new Date(date);
                    const todayObj = new Date();
                    todayObj.setHours(0, 0, 0, 0);
                    dateObj.setHours(0, 0, 0, 0);
                    const isFuture = dateObj > todayObj;

                    return (
                      <Button
                        key={index}
                        variant={isSelected ? "default" : "ghost"}
                        size="sm"
                        onClick={() => handleDateSelect(dateString)}
                        disabled={isFuture}
                        className={`h-8 w-8 p-0 text-sm ${isFuture
                          ? "cursor-not-allowed text-muted-foreground hover:bg-transparent"
                          : isSelected
                            ? "bg-orange-500 text-white hover:bg-orange-600"
                            : isToday
                              ? "bg-orange-100 text-orange-700 hover:bg-orange-200"
                              : "hover:bg-muted"
                          }`}
                        title={isFuture ? "Future dates not available" : ""}
                      >
                        {date.getDate()}
                      </Button>
                    );
                  })}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Today's Intake Summary */}
        <div className="space-y-4">
          {/* Header Card */}
          <div
            className="rounded-2xl border-2 border-border/80 bg-card p-5 shadow-xl"
            style={{
              boxShadow:
                "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
            }}
          >
            <h3 className="text-sm font-semibold text-foreground">
              {selectedDate === getLocalDateString()
                ? "Today's Intake"
                : "Daily Intake"}
            </h3>
            <div className="flex items-baseline space-x-2">
              <span className="text-3xl font-bold text-foreground">
                {Math.round(getTotalsForDate(selectedDate).calories)}
              </span>
              <span className="text-sm text-muted-foreground">
                / {mealGoals.calories_target} cal
              </span>
            </div>
          </div>

          {/* Macros Grid - 2x2 Individual Cards */}
          <div className="grid grid-cols-2 gap-3">
            {/* Protein Card */}
            <div
              className="rounded-2xl border-2 border-border/80 bg-card p-4 shadow-lg"
              style={{
                boxShadow:
                  "0 8px 20px -4px rgba(0, 0, 0, 0.08), 0 6px 8px -5px rgba(0, 0, 0, 0.08)",
              }}
            >
              <div className="flex items-center gap-3">
                <div className="relative h-16 w-16 flex-shrink-0">
                  <svg className="h-16 w-16 -rotate-90 transform">
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      stroke="currentColor"
                      strokeWidth="6"
                      fill="none"
                      className="text-blue-500/20"
                    />
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      stroke="currentColor"
                      strokeWidth="6"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 28}`}
                      strokeDashoffset={`${2 * Math.PI * 28 * (1 - Math.min(getTotalsForDate(selectedDate).protein / mealGoals.protein_g, 1))}`}
                      className="text-blue-500 transition-all duration-300"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-bold text-muted-foreground">
                      {Math.round(
                        (getTotalsForDate(selectedDate).protein /
                          mealGoals.protein_g) *
                        100
                      )}
                      %
                    </span>
                  </div>
                </div>
                <div className="flex flex-col">
                  <p className="text-sm font-medium text-foreground">Protein</p>
                  <p className="text-lg font-bold text-foreground">
                    {Math.round(getTotalsForDate(selectedDate).protein)}g
                  </p>
                  <p className="text-xs text-foreground">
                    Goal: {mealGoals.protein_g}g
                  </p>
                </div>
              </div>
            </div>

            {/* Carbs Card */}
            <div
              className="rounded-2xl border-2 border-border/80 bg-card p-4 shadow-lg"
              style={{
                boxShadow:
                  "0 8px 20px -4px rgba(0, 0, 0, 0.08), 0 6px 8px -5px rgba(0, 0, 0, 0.08)",
              }}
            >
              <div className="flex items-center gap-3">
                <div className="relative h-16 w-16 flex-shrink-0">
                  <svg className="h-16 w-16 -rotate-90 transform">
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      stroke="currentColor"
                      strokeWidth="6"
                      fill="none"
                      className="text-green-500/20"
                    />
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      stroke="currentColor"
                      strokeWidth="6"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 28}`}
                      strokeDashoffset={`${2 * Math.PI * 28 * (1 - Math.min(getTotalsForDate(selectedDate).carbs / mealGoals.carbs_g, 1))}`}
                      className="text-green-500 transition-all duration-300"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-bold text-muted-foreground">
                      {Math.round(
                        (getTotalsForDate(selectedDate).carbs /
                          mealGoals.carbs_g) *
                        100
                      )}
                      %
                    </span>
                  </div>
                </div>
                <div className="flex flex-col">
                  <p className="text-sm font-medium text-foreground"> Carbs</p>
                  <p className="text-lg font-bold text-foreground">
                    {Math.round(getTotalsForDate(selectedDate).carbs)}g
                  </p>
                  <p className="text-xs text-foreground">
                    Goal: {mealGoals.carbs_g}g
                  </p>
                </div>
              </div>
            </div>

            {/* Fat Card */}
            <div
              className="rounded-2xl border-2 border-border/80 bg-card p-4 shadow-lg"
              style={{
                boxShadow:
                  "0 8px 20px -4px rgba(0, 0, 0, 0.08), 0 6px 8px -5px rgba(0, 0, 0, 0.08)",
              }}
            >
              <div className="flex items-center gap-3">
                <div className="relative h-16 w-16 flex-shrink-0">
                  <svg className="h-16 w-16 -rotate-90 transform">
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      stroke="currentColor"
                      strokeWidth="6"
                      fill="none"
                      className="text-orange-500/20"
                    />
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      stroke="currentColor"
                      strokeWidth="6"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 28}`}
                      strokeDashoffset={`${2 * Math.PI * 28 * (1 - Math.min(getTotalsForDate(selectedDate).fat / mealGoals.fat_g, 1))}`}
                      className="text-orange-500 transition-all duration-300"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-bold text-muted-foreground">
                      {Math.round(
                        (getTotalsForDate(selectedDate).fat / mealGoals.fat_g) *
                        100
                      )}
                      %
                    </span>
                  </div>
                </div>
                <div className="flex flex-col">
                  <p className="text-sm font-medium text-foreground">Fat</p>
                  <p className="text-lg font-bold text-foreground">
                    {Math.round(getTotalsForDate(selectedDate).fat)}g
                  </p>
                  <p className="text-xs text-foreground">
                    Goal: {mealGoals.fat_g}g
                  </p>
                </div>
              </div>
            </div>

            {/* Fiber Card */}
            <div
              className="rounded-2xl border-2 border-border/80 bg-card p-4 shadow-lg"
              style={{
                boxShadow:
                  "0 8px 20px -4px rgba(0, 0, 0, 0.08), 0 6px 8px -5px rgba(0, 0, 0, 0.08)",
              }}
            >
              <div className="flex items-center gap-3">
                <div className="relative h-16 w-16 flex-shrink-0">
                  <svg className="h-16 w-16 -rotate-90 transform">
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      stroke="currentColor"
                      strokeWidth="6"
                      fill="none"
                      className="text-purple-500/20"
                    />
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      stroke="currentColor"
                      strokeWidth="6"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 28}`}
                      strokeDashoffset={`${2 * Math.PI * 28 * (1 - Math.min(getTotalsForDate(selectedDate).fiber / mealGoals.fiber_g, 1))}`}
                      className="text-purple-500 transition-all duration-300"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-bold text-muted-foreground">
                      {Math.round(
                        (getTotalsForDate(selectedDate).fiber /
                          mealGoals.fiber_g) *
                        100
                      )}
                      %
                    </span>
                  </div>
                </div>
                <div className="flex flex-col">
                  <p className="text-sm font-medium text-foreground">Fiber</p>
                  <p className="text-lg font-bold text-foreground">
                    {Math.round(getTotalsForDate(selectedDate).fiber)}g
                  </p>
                  <p className="text-xs text-foreground">
                    Goal: {mealGoals.fiber_g}g
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats Row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50 to-orange-100 p-3 text-center shadow-sm dark:border-orange-800 dark:from-orange-950 dark:to-orange-900">
              <div className="text-xl font-bold text-orange-700 dark:text-orange-300">
                {Object.values(groupedMeals).flat().length}
              </div>
              <div className="text-xs font-medium text-orange-600 dark:text-orange-400">
                Meals
              </div>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-emerald-100 p-3 text-center shadow-sm dark:border-emerald-800 dark:from-emerald-950 dark:to-emerald-900">
              <div className="text-xl font-bold text-emerald-700 dark:text-emerald-300">
                {Math.round(getTotalsForDate(selectedDate).net_carbs || 0)}g
              </div>
              <div className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                Net Carbs
              </div>
            </div>
          </div>
        </div>

        {/* Meal Log Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-foreground">Meal Log</h3>
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-lg border-orange-200 bg-gradient-to-r from-orange-50 to-orange-100 text-xs font-medium text-orange-700 transition-all duration-200 hover:border-orange-300 hover:from-orange-100 hover:to-orange-200 hover:text-orange-800 dark:border-orange-800 dark:from-orange-950 dark:to-orange-900 dark:text-orange-300 dark:hover:border-orange-700 dark:hover:from-orange-900 dark:hover:to-orange-800"
              onClick={() =>
                navigate(`/glucera${GluceraNavigationRoutes.MEAL_HISTORY}`)
              }
            >
              View History
            </Button>
          </div>

          {/* Meal Type Cards */}
          <div className="space-y-3">
            {/* Render individual meal cards for each meal */}
            {[
              ...groupedMeals.breakfast.map((meal, index) => ({
                type: "breakfast",
                label: "Breakfast",
                meal: meal,
                index: index,
              })),
              ...groupedMeals.lunch.map((meal, index) => ({
                type: "lunch",
                label: "Lunch",
                meal: meal,
                index: index,
              })),
              ...groupedMeals.snack.map((meal, index) => ({
                type: "snack",
                label: "Snack",
                meal: meal,
                index: index,
              })),
              ...groupedMeals.dinner.map((meal, index) => ({
                type: "dinner",
                label: "Dinner",
                meal: meal,
                index: index,
              })),
              // Add empty meal cards if no meals exist for that type
              ...(groupedMeals.breakfast.length === 0
                ? [
                  {
                    type: "breakfast",
                    label: "Breakfast",
                    meal: null,
                    index: 0,
                  },
                ]
                : []),
              ...(groupedMeals.lunch.length === 0
                ? [{ type: "lunch", label: "Lunch", meal: null, index: 0 }]
                : []),
              ...(groupedMeals.snack.length === 0
                ? [{ type: "snack", label: "Snack", meal: null, index: 0 }]
                : []),
              ...(groupedMeals.dinner.length === 0
                ? [{ type: "dinner", label: "Dinner", meal: null, index: 0 }]
                : []),
            ]
              .sort((a, b) => {
                const order: Record<string, number> = {
                  breakfast: 0,
                  lunch: 1,
                  snack: 2,
                  dinner: 3,
                };
                return order[a.type] - order[b.type] || a.index - b.index;
              })
              .map(({ type, label, meal, index }) => (
                <div
                  key={`${type}-${index}`}
                  onClick={
                    meal ? () => handleMealTypeClick(type, [meal]) : undefined
                  }
                  className={`rounded-2xl border border-border bg-card p-4 shadow-sm transition-all duration-200 ${meal
                    ? "cursor-pointer hover:shadow-md active:scale-[0.98]"
                    : "cursor-default opacity-60"
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                        {getMealIcon(type)}

                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground">
                          {label}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {meal ? getMealTime(type, [meal]) : ""}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {meal && typeof meal.ghi_score === "number" && (
                        <div className="mr-2 flex items-baseline text-sm font-bold">
                          <span
                            className={
                              meal.ghi_score >= 80
                                ? "text-green-500"
                                : meal.ghi_score >= 50
                                  ? "text-yellow-500"
                                  : "text-orange-500"
                            }
                          >
                            {Math.round(meal.ghi_score)}
                          </span>
                          <span className="ml-0.5 text-[10px] text-muted-foreground">
                            /100
                          </span>
                        </div>
                      )}
                      <RightOutlined className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>

                  {/* Food Items Preview */}
                  {meal && meal.food_items && meal.food_items.length > 0 && (
                    <div className="ml-13 mt-4">
                      <div className="space-y-1 text-xs leading-relaxed text-muted-foreground">
                        {meal.food_items.slice(0, 3).map((item, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <span className="mt-0.5 text-[10px] text-muted-foreground/60">
                              •
                            </span>
                            <span className="flex-1 font-semibold text-muted-foreground">
                              {item.name}
                            </span>
                          </div>
                        ))}
                        {meal.food_items.length > 3 && (
                          <div className="mt-1 flex items-start gap-2 text-muted-foreground/60">
                            <span className="mt-0.5 text-[10px]">•</span>
                            <span className="flex-1 text-xs font-semibold">
                              +{meal.food_items.length - 3} more items
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      </div>

      <BottomNavBar activeIndex={1} />
    </div>
  );
};

export default MealPage;
